/** GPU Gaussian Blur — separable 2-pass fragment-shader port of
 *  `lib/filters/gaussian-blur.ts`.
 *
 *  Pass 0 (horizontal): each output pixel reads 2r+1 source taps
 *  along the row, weighted by the 1D Gaussian kernel.
 *  Pass 1 (vertical): same kernel applied along columns.
 *
 *  The 2D Gaussian is separable, so applying the 1D kernel twice
 *  produces the same result as the full r²-tap 2D convolution
 *  for a fraction of the work — O(r) per pixel per pass instead
 *  of O(r²). This is the standard trick every image-editor uses.
 *
 *  Kernel weights come from `buildGaussianKernel1D` (the CPU
 *  authoritative source). We pass them as a `uniform float[MAX]`
 *  array and let the shader loop from -radius to +radius. MAX is
 *  sized for the editor's max-200 UI cap (radius 200 → 401-tap
 *  kernel). Smaller radii leave the tail of the uniform array
 *  unread.
 *
 *  Alpha is preserved unchanged — same convention as the CPU path
 *  to avoid halos around already-transparent regions.
 *
 *  Identity (radius=0) short-circuits to a canvas2D copy via the
 *  shared `applyIdentityCopy` helper, matching the JS path. */

import { buildGaussianKernel1D, type GaussianBlurValue } from '../../filters/gaussian-blur';
import { MAX_KERNEL_RADIUS } from '../blur-shared';
import { applyIdentityCopy } from '../identity-copy';
import { applyShaderPasses } from '../runtime';

export const MAX_KERNEL_SIZE = 2 * MAX_KERNEL_RADIUS + 1;

/** Horizontal 1D convolution. Loops -radius..+radius, samples
 *  along the row, accumulates RGB weighted by kernel. Alpha is
 *  copied from the center tap.
 *
 *  Exported so the sharpen-family kinds (Unsharp Mask, Smart
 *  Sharpen) can reuse the identical Gaussian-blur passes as their
 *  first two passes — same shader source ⇒ same program-cache
 *  entry ⇒ byte parity by construction. */
export const GAUSSIAN_HORIZONTAL_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_radius;
uniform float u_kernel[${MAX_KERNEL_SIZE}];
out vec4 outColor;

void main() {
  // The integer pixel center for the OUTPUT we're computing.
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = v_uv.y * u_dims.y - 0.5;
  vec3 sum = vec3(0.0);
  // Center tap supplies the alpha channel (matches JS, which copies
  // src[di + 3] from the same-position input pixel).
  vec4 center = texture(u_source, v_uv);
  for (int k = -${MAX_KERNEL_RADIUS}; k <= ${MAX_KERNEL_RADIUS}; k++) {
    if (k < -u_radius || k > u_radius) continue;
    float sxClamped = clamp(pxX + float(k), 0.0, u_dims.x - 1.0);
    vec2 uv = vec2((sxClamped + 0.5) / u_dims.x, (pxY + 0.5) / u_dims.y);
    vec3 c = texture(u_source, uv).rgb;
    sum += c * u_kernel[k + ${MAX_KERNEL_RADIUS}];
  }
  outColor = vec4(sum, center.a);
}
`;

/** Vertical 1D convolution. Identical shape to horizontal but
 *  samples along the column. The two shaders are split (rather
 *  than one shader with a direction uniform) so the GPU's branch
 *  predictor stays warm — same rationale the JS path gives.
 *
 *  Exported alongside `GAUSSIAN_HORIZONTAL_FRAG` so the sharpen
 *  family can reuse it verbatim. */
export const GAUSSIAN_VERTICAL_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_radius;
uniform float u_kernel[${MAX_KERNEL_SIZE}];
out vec4 outColor;

void main() {
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = v_uv.y * u_dims.y - 0.5;
  vec3 sum = vec3(0.0);
  vec4 center = texture(u_source, v_uv);
  for (int k = -${MAX_KERNEL_RADIUS}; k <= ${MAX_KERNEL_RADIUS}; k++) {
    if (k < -u_radius || k > u_radius) continue;
    float syClamped = clamp(pxY + float(k), 0.0, u_dims.y - 1.0);
    vec2 uv = vec2((pxX + 0.5) / u_dims.x, (syClamped + 0.5) / u_dims.y);
    vec3 c = texture(u_source, uv).rgb;
    sum += c * u_kernel[k + ${MAX_KERNEL_RADIUS}];
  }
  outColor = vec4(sum, center.a);
}
`;

/** Pad a Float32Array kernel to MAX_KERNEL_SIZE so the uniform
 *  array's length always matches the shader's declared array
 *  size. Trailing zeros don't contribute to the sum because the
 *  shader skips them via `if (k < -u_radius || k > u_radius)`.
 *
 *  Exported so the sharpen family can reuse the same padding —
 *  the kernel they hand to GAUSSIAN_HORIZONTAL_FRAG /
 *  GAUSSIAN_VERTICAL_FRAG must match the shader's array length. */
export function padGaussianKernel(kernel: Float32Array): number[] {
  const padded = new Array<number>(MAX_KERNEL_SIZE).fill(0);
  const offset = (MAX_KERNEL_SIZE - kernel.length) / 2;
  for (let i = 0; i < kernel.length; i++) {
    padded[offset + i] = kernel[i];
  }
  return padded;
}

export function applyGaussianBlurOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: GaussianBlurValue,
): boolean {
  const radius = Math.max(0, Math.floor(value.radius));
  if (radius === 0) return applyIdentityCopy(source, target);
  if (radius > MAX_KERNEL_RADIUS) {
    // Defensive — UI caps at 200 but a programmatic caller could
    // exceed it. Fall back to CPU rather than silently truncate.
    return false;
  }
  const kernel = buildGaussianKernel1D(radius);
  const paddedKernel = padGaussianKernel(kernel);
  const dims: [number, number] = [source.width, source.height];
  return applyShaderPasses({
    source,
    target,
    passes: [
      {
        fragShaderSrc: GAUSSIAN_HORIZONTAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radius },
          u_kernel: { type: 'float[]', value: paddedKernel },
        },
      },
      {
        fragShaderSrc: GAUSSIAN_VERTICAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radius },
          u_kernel: { type: 'float[]', value: paddedKernel },
        },
      },
    ],
  });
}
